import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Search, Package, Loader2, AlertCircle, AlertTriangle, Repeat, CircleDot, Plus } from 'lucide-react';
import { useClinic } from '@/contexts/ClinicContext';
import { useGuidedOrders } from '@/hooks/useGuidedOrders';
import { GuidedOrderStatusBadge, GuidedPaymentStatusBadge } from '@/components/guided-order/GuidedOrderStatusBadge';
import { Badge } from '@/components/ui/badge';
import { GuidedOrderDetailSheet } from '@/components/guided-order/GuidedOrderDetailSheet';
import { GuidedIntakeModal } from '@/components/clinic/GuidedIntakeModal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { GuidedOrderStatus } from '@/types/guidedOrder';
import { useQueryClient } from '@tanstack/react-query';

const TAB_STATUS_MAP: Record<string, GuidedOrderStatus[] | undefined> = {
  all: undefined,
  intake_pending: ['awaiting_intake'],
  intake_completed: ['intake_completed'],
  under_review: ['mdi_submitted', 'mdi_completed'],
  verified: ['rx_verified'],
  amendment: undefined, // uses payment_status filter instead
  fulfilled: ['fulfilled'],
  cancelled: ['cancelled', 'expired'],
};

const TAB_PAYMENT_STATUS_MAP: Record<string, string[] | undefined> = {
  amendment: ['pending_amendment'],
};

const TAB_LABELS: Record<string, string> = {
  all: 'All',
  intake_pending: 'Enrollment Pending',
  intake_completed: 'Enrollment Completed',
  under_review: 'Under Review',
  verified: 'Verified',
  amendment: 'Pending Amendment',
  fulfilled: 'Fulfilled',
  cancelled: 'Cancelled / Expired',
};

const LIMIT = 20;

function formatAmount(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function patientFullName(patient: { first_name: string; middle_name?: string; last_name: string }): string {
  return [patient.first_name, patient.middle_name, patient.last_name].filter(Boolean).join(' ');
}

function formatOrderNumber(n?: number): string {
  return n != null ? `GO-${n}` : '—';
}

function holdExpiryText(holdExpiresAt: string | null): string {
  if (!holdExpiresAt) return '—';
  const d = new Date(holdExpiresAt);
  const now = new Date();
  const days = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'Expired';
  if (days === 0) return 'Expires today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

export default function GuidedOrders() {
  const { clinic, isDirect } = useClinic();
  const { orderId: orderIdFromRoute } = useParams<{ orderId?: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (orderIdFromRoute) {
      setDetailOrderId(orderIdFromRoute);
      setDetailOpen(true);
    }
  }, [orderIdFromRoute]);

  // Debounce search input by 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setOffset(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const statuses = TAB_STATUS_MAP[activeTab];
  const paymentStatuses = TAB_PAYMENT_STATUS_MAP[activeTab];
  const filters = useMemo(
    () => ({
      clinic_id: clinic?.id ?? '',
      status: statuses,
      payment_status: paymentStatuses,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      limit: LIMIT,
      offset,
    }),
    [clinic?.id, statuses, paymentStatuses, debouncedSearch, offset]
  );

  const { data, isLoading, error } = useGuidedOrders(filters);
  const orders = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;

  const handleRowClick = (orderId: string) => {
    setDetailOrderId(orderId);
    setDetailOpen(true);
  };

  if (isDirect || !clinic?.id) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Guided Orders are not available for direct clinics. This clinic does not have guided ordering enabled.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Guided Orders</h1>
          <p className="text-slate-600">View and manage guided order lifecycles</p>
        </div>
        <Button onClick={() => setEnrollOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          New Guided Order
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load guided orders. Please try again.</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Guided order list
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by patient or order ID..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={v => (setActiveTab(v), setOffset(0))}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              {Object.entries(TAB_LABELS).map(([value, label]) => (
                <TabsTrigger key={value} value={value}>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="mt-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No orders found</h3>
                  <p className="text-slate-600">
                    {searchTerm
                      ? 'Try adjusting your search.'
                      : 'No guided orders in this tab.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3 font-medium text-slate-700">Order ID</th>
                          <th className="text-left p-3 font-medium text-slate-700">Patient</th>
                          <th className="text-left p-3 font-medium text-slate-700">Program</th>
                          <th className="text-left p-3 font-medium text-slate-700">Billing Type</th>
                          <th className="text-left p-3 font-medium text-slate-700">Amount</th>
                          <th className="text-left p-3 font-medium text-slate-700">Status</th>
                          <th className="text-left p-3 font-medium text-slate-700">Payment</th>
                          <th className="text-left p-3 font-medium text-slate-700">Date</th>
                          <th className="text-left p-3 font-medium text-slate-700">Hold expiry</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map(order => (
                          <tr
                            key={order.id}
                            className="border-b hover:bg-slate-50 cursor-pointer transition-colors"
                            onClick={() => handleRowClick(order.id)}
                          >
                            <td className="p-3">
                              <span className="font-mono text-xs font-medium text-blue-600">
                                {formatOrderNumber(order.order_number)}
                              </span>
                            </td>
                            <td className="p-3">
                              {order.patient
                                ? patientFullName(order.patient)
                                : order.patient_id}
                            </td>
                            <td className="p-3">
                              {(order.metadata?.program_label as string) || order.clinic?.name || '—'}
                            </td>
                            <td className="p-3">
                              {order.clinicProgramOption?.is_subscription ? (
                                <Badge variant="outline" className="bg-violet-100 text-violet-800 border-violet-300 border gap-1">
                                  <Repeat className="h-3 w-3" />
                                  {order.clinicProgramOption.subscription_frequency
                                    ? order.clinicProgramOption.subscription_frequency.charAt(0).toUpperCase() + order.clinicProgramOption.subscription_frequency.slice(1)
                                    : 'Recurring'}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300 border gap-1">
                                  <CircleDot className="h-3 w-3" />
                                  One-time
                                </Badge>
                              )}
                            </td>
                            <td className="p-3">{formatAmount(order.amount_cents)}</td>
                            <td className="p-3">
                              <GuidedOrderStatusBadge
                                status={order.status}
                                hasPendingCancellation={order.cancellationRequests?.some((cr: any) => cr.status === 'pending')}
                              />
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-1.5">
                                <GuidedPaymentStatusBadge paymentStatus={order.payment_status} />
                                {order.payment_status === 'pending_amendment' && (
                                  <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <div>{formatDate(order.created_at)}</div>
                              <div className="text-xs text-slate-500">{formatTime(order.created_at)}</div>
                            </td>
                            <td className="p-3">
                              {order.hold_expires_at
                                ? holdExpiryText(order.hold_expires_at)
                                : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-slate-600">
                        Page {currentPage} of {totalPages} ({total} total)
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={offset === 0}
                          onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={offset + LIMIT >= total}
                          onClick={() => setOffset(offset + LIMIT)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Tabs>
        </CardContent>
      </Card>

      <GuidedOrderDetailSheet
        orderId={detailOrderId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />

      {clinic?.id && (
        <GuidedIntakeModal
          open={enrollOpen}
          onOpenChange={setEnrollOpen}
          clinicId={clinic.id}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['guided-orders'] });
          }}
        />
      )}
    </div>
  );
}
